/**
 * Los dos dialogos que mueven un cheque: depositarlo y rechazarlo.
 *
 * DEPOSITAR
 * ---------
 * El backend exige que la moneda del cheque coincida con la de la cuenta
 * ("El cheque es en ARS y la cuenta Efectivo opera en USD"). En vez de
 * dejar elegir una cuenta imposible y contestar con un error rojo, el
 * desplegable solo ofrece las cuentas que pueden recibirlo. Un desplegable que
 * ofrece opciones que van a fallar no es un desplegable, es una trampa.
 *
 * RECHAZAR
 * --------
 * Un rechazo no es "marcar el cheque como malo": revierte el ingreso de caja y
 * le devuelve la deuda al cliente, porque el cobro nunca ocurrio realmente. Eso
 * se dice en el dialogo, antes de apretar, y no en una nota al pie.
 */
import { useEffect, useState } from "react";
import { Ban, CircleCheck, Landmark, Send } from "lucide-react";

import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InputImporte } from "@/components/money/input-importe";
import { Segmented } from "@/components/ui/segmented";
import { SelectNativo } from "@/components/ui/select-nativo";
import { Textarea } from "@/components/ui/textarea";
import { aNumero, type CuentaFinanciera, type SaldoProveedor } from "@/lib/tipos";
import { formatearFecha } from "@/lib/utils";

import { Fila } from "./DialogMovimiento";
import { cuentasDeDeposito, firmanteCheque, type ChequeCartera } from "./logica";

// ─────────────────────────────────────────────────────────────────────────────
// Depositar
// ─────────────────────────────────────────────────────────────────────────────

export interface DialogDepositarProps {
  cheque: ChequeCartera | null;
  onCerrar: () => void;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  guardando?: boolean;
  onConfirmar: (datos: { id: number; cuenta_id: number }) => void;
}

export function DialogDepositar({
  cheque,
  onCerrar,
  cuentas,
  guardando = false,
  onConfirmar,
}: DialogDepositarProps): JSX.Element {
  const [cuentaId, setCuentaId] = useState<string>("");

  const compatibles = cheque ? cuentasDeDeposito(cuentas, cheque.moneda) : [];

  // Al cambiar de cheque se reelige la cuenta: la que estaba seleccionada podia
  // ser de la otra moneda y ya no sirve.
  useEffect(() => {
    if (!cheque) return;
    const primera = cuentasDeDeposito(cuentas, cheque.moneda)[0];
    setCuentaId(primera ? String(primera.id) : "");
  }, [cheque, cuentas]);

  const cuenta = compatibles.find((c) => String(c.id) === cuentaId);
  const importe = cheque ? aNumero(cheque.importe) : 0;

  return (
    <Dialog open={cheque !== null} onOpenChange={(a) => (a ? undefined : onCerrar())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Depositar cheque</DialogTitle>
          {/* Decia «El importe entra en la cuenta que elijas», y el de un cobro ya
              habia entrado al cobrarlo (0068): depositarlo solo lo mueve de
              cuenta, o no mueve nada si es la misma. Solo un cheque cargado a
              mano, sin cobro detras, suma recien ahora. */}
          <DialogDescription>
            {cheque && cheque.movimiento_id == null
              ? "El cheque pasa a DEPOSITADO y su importe entra en la cuenta que elijas."
              : "El cheque pasa a DEPOSITADO. Su importe ya está en la cuenta donde lo recibiste; si elegís otra, la plata se mueve ahí."}
          </DialogDescription>
        </DialogHeader>

        {cheque ? (
          <div className="space-y-4">
            <Card variant="muted" padding="normal">
              <dl className="space-y-2">
                <Fila
                  termino="Cheque"
                  valor={
                    <>
                      {cheque.tipo === "ECHEQ" ? "E-cheq" : "Cheque"}{" "}
                      <Codigo>{cheque.numero}</Codigo>
                    </>
                  }
                />
                <Fila termino="Banco" valor={cheque.banco ?? "—"} />
                <Fila termino="Librador" valor={firmanteCheque(cheque)} />
                <Fila termino="Se cobra" valor={formatearFecha(cheque.fecha_cobro)} />
                <Fila
                  termino="Importe"
                  valor={
                    <Importe monto={importe} moneda={cheque.moneda} className="font-semibold" />
                  }
                />
              </dl>
            </Card>

            {compatibles.length === 0 ? (
              <Aviso tono="destructive">
                No hay ningún banco en {cheque.moneda} para depositar este cheque. Creá uno en
                Configuración antes de depositarlo.
              </Aviso>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="dep-cuenta">Cuenta de depósito</Label>
                <Select value={cuentaId} onValueChange={setCuentaId}>
                  <SelectTrigger id="dep-cuenta" aria-label="Cuenta de depósito">
                    <SelectValue placeholder="Elegí la cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    {compatibles.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.nombre} · {c.moneda}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Solo se listan los bancos en {cheque.moneda}: un cheque se deposita en un banco de
                  su moneda, no en la caja ni en una billetera.
                </p>
              </div>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            cargando={guardando}
            disabled={!cheque || !cuenta}
            onClick={() => {
              if (!cheque || !cuenta) return;
              onConfirmar({ id: cheque.id, cuenta_id: cuenta.id });
            }}
          >
            <Landmark aria-hidden="true" />
            Confirmar depósito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Rechazar
// ─────────────────────────────────────────────────────────────────────────────

export interface DialogRechazarProps {
  cheque: ChequeCartera | null;
  onCerrar: () => void;
  guardando?: boolean;
  onConfirmar: (datos: { id: number; motivo: string }) => void;
}

export function DialogRechazar({
  cheque,
  onCerrar,
  guardando = false,
  onConfirmar,
}: DialogRechazarProps): JSX.Element {
  const [motivo, setMotivo] = useState("");

  useEffect(() => {
    if (cheque) setMotivo("");
  }, [cheque]);

  const importe = cheque ? aNumero(cheque.importe) : 0;
  const listo = motivo.trim().length > 0;

  return (
    <Dialog open={cheque !== null} onOpenChange={(a) => (a ? undefined : onCerrar())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rechazar cheque</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-1">
            {cheque ? (
              <>
                <Codigo>{cheque.numero}</Codigo> · {cheque.banco ?? "sin banco"} ·{" "}
                <Importe monto={importe} moneda={cheque.moneda} />
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Aviso tono="destructive" titulo="Esto le devuelve la deuda al cliente.">
            Rechazar no es marcar el cheque como malo y listo: revierte el ingreso de caja y vuelve
            a cargar el saldo en la cuenta corriente
            {cheque?.cliente ? ` de ${cheque.cliente}` : ""}, porque el cobro nunca ocurrió
            realmente.
          </Aviso>

          <Campo
            label="Motivo"
            requerido
            ayuda="El motivo queda escrito en el cheque y en el ajuste de la cuenta corriente. Dentro de seis meses es lo único que va a explicar ese saldo."
          >
            <Textarea
              id="rech-motivo"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Sin fondos, firma no coincide, cuenta cerrada…"
            />
          </Campo>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            cargando={guardando}
            disabled={!cheque || !listo}
            onClick={() => {
              if (!cheque) return;
              onConfirmar({ id: cheque.id, motivo: motivo.trim() });
            }}
          >
            <Ban aria-hidden="true" />
            Confirmar rechazo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Endosar (0155)
// ─────────────────────────────────────────────────────────────────────────────

export interface DatosEndoso {
  id: number;
  proveedor_id: number;
  moneda_deuda?: "ARS" | "USD";
  cotizacion?: number;
}

export interface DialogEndosarProps {
  cheque: ChequeCartera | null;
  proveedores: ReadonlyArray<
    Pick<SaldoProveedor, "proveedor_id" | "nombre" | "saldo_ars" | "saldo_usd">
  >;
  onCerrar: () => void;
  guardando?: boolean;
  onConfirmar: (datos: DatosEndoso) => void;
}

/**
 * Entregarle un cheque de la cartera a un proveedor como pago. Sale de la
 * cuenta donde entro, baja lo que se le debe y queda «Entregado». Si la deuda
 * esta en la otra moneda, el dolar lo dice el proveedor, no el sistema.
 */
export function DialogEndosar({
  cheque,
  proveedores,
  onCerrar,
  guardando = false,
  onConfirmar,
}: DialogEndosarProps): JSX.Element {
  // Arranca limpio con cada cheque: quien lo monta le pone `key` por cheque.
  const [proveedorId, setProveedorId] = useState("");
  const [monedaDeuda, setMonedaDeuda] = useState<"ARS" | "USD">(cheque?.moneda ?? "ARS");
  const [cotizacion, setCotizacion] = useState<number | null>(null);

  const proveedor = proveedores.find((p) => String(p.proveedor_id) === proveedorId);
  const convierte = cheque !== null && monedaDeuda !== cheque.moneda;
  const listo = Boolean(
    cheque && proveedor && (!convierte || (cotizacion !== null && cotizacion > 0)),
  );
  const importe = cheque ? aNumero(cheque.importe) : 0;

  return (
    <Dialog open={cheque !== null} onOpenChange={(a) => (a ? undefined : onCerrar())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Endosar cheque a un proveedor</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-1">
            {cheque ? (
              <>
                <Codigo>{cheque.numero}</Codigo> · {cheque.banco ?? "sin banco"} ·{" "}
                <Importe monto={importe} moneda={cheque.moneda} />
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Campo label="A quién se le entrega" requerido>
            <SelectNativo
              id="endoso-proveedor"
              value={proveedorId}
              onChange={(e) => setProveedorId(e.target.value)}
            >
              <option value="" disabled>
                Elegí el proveedor
              </option>
              {proveedores.map((p) => (
                <option key={p.proveedor_id} value={String(p.proveedor_id)}>
                  {p.nombre}
                </option>
              ))}
            </SelectNativo>
          </Campo>

          <div className="space-y-1.5">
            <Label>¿Contra qué deuda va?</Label>
            <Segmented
              etiqueta="Moneda de la deuda"
              apariencia="chips"
              tamano="sm"
              opciones={[
                { valor: "ARS", etiqueta: "En pesos" },
                { valor: "USD", etiqueta: "En dólares" },
              ]}
              valor={monedaDeuda}
              onChange={(v) => setMonedaDeuda(v as "ARS" | "USD")}
            />
            {proveedor ? (
              <p className="text-xs text-muted-foreground">
                Se le debe <Importe monto={aNumero(proveedor.saldo_ars)} moneda="ARS" tamano="sm" />{" "}
                y <Importe monto={aNumero(proveedor.saldo_usd)} moneda="USD" tamano="sm" />.
              </p>
            ) : null}
          </div>

          {convierte ? (
            <InputImporte
              label="Dólar que usa el proveedor"
              valor={cotizacion}
              onValorChange={setCotizacion}
              moneda="ARS"
              requerido
            />
          ) : null}

          <Aviso tono="neutral" compacto>
            El cheque sale de la cuenta donde entró y queda como entregado. Si después rebota, se
            arregla con el proveedor en su ficha y con el cliente con un ajuste.
          </Aviso>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            cargando={guardando}
            disabled={!listo}
            onClick={() => {
              if (!cheque || !proveedor) return;
              onConfirmar({
                id: cheque.id,
                proveedor_id: proveedor.proveedor_id,
                ...(convierte
                  ? { moneda_deuda: monedaDeuda, cotizacion: cotizacion ?? undefined }
                  : {}),
              });
            }}
          >
            <Send aria-hidden="true" />
            Endosar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Se acreditó (0155)
// ─────────────────────────────────────────────────────────────────────────────

export interface DialogAcreditarProps {
  cheque: ChequeCartera | null;
  onCerrar: () => void;
  guardando?: boolean;
  onConfirmar: (datos: { id: number }) => void;
}

/**
 * Confirmar que el cheque se acreditó. No mueve plata (ya estaba en el saldo
 * desde que se recibió), pero es definitivo: después no se puede rechazar.
 */
export function DialogAcreditar({
  cheque,
  onCerrar,
  guardando = false,
  onConfirmar,
}: DialogAcreditarProps): JSX.Element {
  const importe = cheque ? aNumero(cheque.importe) : 0;
  return (
    <Dialog open={cheque !== null} onOpenChange={(a) => (a ? undefined : onCerrar())}>
      <DialogContent tamano="sm" mobile="hoja">
        <DialogHeader>
          <DialogTitle>¿Se acreditó este cheque?</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-1">
            {cheque ? (
              <>
                <Codigo>{cheque.numero}</Codigo> · {cheque.banco ?? "sin banco"} ·{" "}
                <Importe monto={importe} moneda={cheque.moneda} />
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <Aviso tono="neutral" compacto>
          Marcalo cuando lo veas en el extracto del banco. No mueve plata: ya estaba en el saldo
          desde que se recibió. Pasa a «Cobrados» y después no se puede rechazar.
        </Aviso>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Volver
          </Button>
          <Button
            type="button"
            cargando={guardando}
            disabled={!cheque}
            onClick={() => cheque && onConfirmar({ id: cheque.id })}
          >
            <CircleCheck aria-hidden="true" />
            Sí, se acreditó
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
